import { Link } from "react-router-dom";
import { useUpcomingMilestones } from "@/hooks/use-upcoming-milestones";
import type { UpcomingMilestone } from "@/hooks/use-upcoming-milestones";
import { upcomingMilestones as fallbackMilestones } from "@/data/milestones";
import { formatShortDate } from "@/lib/date-format";

/**
 * SPEC 3.1.2 #2 — closest upcoming milestones, big mono numerals (T-N days).
 * Hot styling (accent border) when ≤7 days.
 *
 * BW7 mobile pass:
 *   - Pulls 6 milestones (was 3) so a horizontal scroll on phones shows the
 *     full near-term horizon at once.
 *   - Cards rendered in a horizontally-scrolling row with `scroll-snap`
 *     behavior so the row feels native on touch.
 *   - Numeral down to 36px on mobile (was 80px+) and card padding cut ~40%.
 *   - Title clamps to 2 lines so a long milestone name can't blow up the
 *     row height.
 *   - Date subtitle uses "Sun 7 Jun" style instead of raw ISO YYYY-MM-DD.
 */

export function CountdownBand() {
  const q = useUpcomingMilestones(6);

  let items: UpcomingMilestone[] | null = null;
  if (q.data && q.data.length > 0) {
    items = q.data;
  } else if (q.isError || (q.isFetched && (q.data?.length ?? 0) === 0)) {
    // DB empty or errored — hardcoded fallback so the band renders something
    // useful. Disappears once the user runs bootstrap_projects_seeds() and
    // ProjectItem milestones land.
    items = fallbackMilestones(new Date(), 6).map((m) => ({
      id: m.id,
      title: m.title,
      date: m.date,
      project: m.project,
      projectId: null,
      importance: m.importance,
      daysUntil: m.daysUntil,
    }));
  }

  if (!items) {
    // initial load — render nothing rather than flash a skeleton
    return null;
  }

  if (items.length === 0) {
    return null; // SPEC: countdown band hidden when nothing upcoming
  }

  return (
    <section
      aria-label="Upcoming milestones"
      className="haus-countdown-band"
    >
      <style>{`
        .haus-countdown-band {
          display: grid;
          grid-auto-flow: column;
          grid-auto-columns: minmax(220px, 1fr);
          gap: 12px;
          margin-bottom: var(--space-6);
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          padding-bottom: 4px;
          -webkit-overflow-scrolling: touch;
        }
        .haus-countdown-band::-webkit-scrollbar { height: 0; }
        .haus-countdown-card {
          padding: 14px 14px 12px;
          background: var(--bg-2);
          border: 1px solid var(--hair);
          border-radius: var(--radius-card);
          scroll-snap-align: start;
          min-width: 0;
          color: inherit;
          text-decoration: none;
          display: block;
          cursor: pointer;
          transition: border-color var(--dur-fast) var(--ease-out), transform var(--dur-fast) var(--ease-out);
        }
        .haus-countdown-card:hover { border-color: var(--hair-2); }
        .haus-countdown-card:active { transform: scale(0.99); }
        .haus-countdown-card.is-hot {
          border-color: var(--accent-dim);
          border-left: 3px solid var(--accent);
          padding-left: 12px;
        }
        .haus-countdown-numeral {
          font-size: 40px;
          line-height: 1;
          font-weight: 500;
          letter-spacing: -0.01em;
        }
        .haus-countdown-title {
          margin-top: 10px;
          font-family: var(--font-sans);
          font-size: 13.5px;
          font-weight: 500;
          line-height: 1.3;
          color: var(--fg);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .haus-countdown-meta {
          margin-top: 6px;
          font-size: 10.5px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--fg-3);
          display: flex;
          gap: 6px;
          align-items: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        @media (max-width: 767px) {
          .haus-countdown-band {
            grid-auto-columns: minmax(160px, 60vw);
            gap: 8px;
            margin-bottom: var(--space-4);
            margin-left: calc(-1 * var(--space-4));
            margin-right: calc(-1 * var(--space-4));
            padding-left: var(--space-4);
            padding-right: var(--space-4);
          }
          .haus-countdown-card { padding: 10px 12px; }
          .haus-countdown-card.is-hot { padding-left: 9px; }
          .haus-countdown-numeral { font-size: 30px; }
          .haus-countdown-title { font-size: 12.5px; margin-top: 6px; }
          .haus-countdown-meta { margin-top: 4px; font-size: 10px; }
        }
      `}</style>
      {items.map((m) => {
        const hot = m.daysUntil <= 7;
        const inner = (
          <>
            <div
              className="mono haus-countdown-numeral"
              style={{ color: hot ? "var(--accent)" : "var(--fg)" }}
            >
              T−{m.daysUntil}
              <span
                style={{
                  fontSize: "var(--type-meta)",
                  color: "var(--fg-3)",
                  marginLeft: 4,
                  fontWeight: 400,
                }}
              >
                d
              </span>
            </div>
            <div className="haus-countdown-title">{m.title}</div>
            <div className="haus-countdown-meta">
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {m.project}
              </span>
              <span style={{ color: "var(--fg-4)" }}>·</span>
              <span style={{ textTransform: "none", letterSpacing: 0 }}>
                {formatShortDate(m.date)}
              </span>
            </div>
          </>
        );
        const cls = `haus-countdown-card${hot ? " is-hot" : ""}`;
        if (m.projectId) {
          return (
            <Link key={m.id} to={`/projects/${m.projectId}`} className={cls}>
              {inner}
            </Link>
          );
        }
        return (
          <article key={m.id} className={cls}>
            {inner}
          </article>
        );
      })}
    </section>
  );
}
